import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "role", "title", "open", "anchorEl", "fullWidth", "placement", "verticalOffset", "disableCloseOnWindowResize", "className", "onClose", "shouldPreventClose", "ariaProps", "modifiers"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useRef, useMemo } from 'react';
import { EmbersMenuList } from '../menuList/menuList';
import { EmbersMenuItem } from '../menuItem/menuItem';
import { EmbersPopper } from '../popper/popper';
import { StyledMenuTitle, StyledTitledMenuList } from './menuStyles';
// the EmbersMenu handles setting focusVisible on the first non-selected
//  item when the menu is first opened (or just the first if it's multiselect)
//
// *** NOTE *** the launch button for this menu should inherit from
//   Mui's ButtonBase - if you want the standard EmbersButton look
//   you can use that and its onClick prop for opening/closing;
//   otherwise use and style the EmbersMenuLaunchButton, and control
//   menu state via onOpenMenuRequest and onCloseMenuRequest
//
export var EmbersMenu = function EmbersMenu(_ref) {
  var children = _ref.children,
    role = _ref.role,
    title = _ref.title,
    open = _ref.open,
    anchorEl = _ref.anchorEl,
    fullWidth = _ref.fullWidth,
    placement = _ref.placement,
    verticalOffset = _ref.verticalOffset,
    disableCloseOnWindowResize = _ref.disableCloseOnWindowResize,
    className = _ref.className,
    onClose = _ref.onClose,
    shouldPreventClose = _ref.shouldPreventClose,
    ariaProps = _ref.ariaProps,
    modifiers = _ref.modifiers,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var handleClose = function handleClose() {
    if (onClose) {
      onClose();
    }
  };
  var menuItemsCountRef = useRef(0);
  var menuItemsRef = useRef([]);
  var focusOnMenuItem = function focusOnMenuItem() {
    if (anchorEl && anchorEl.classList.contains('Mui-focusVisible') && menuItemsRef.current.length) {
      var _loop = function _loop() {
        var menuItemRef = menuItemsRef.current[i];
        // take the first non-disabled item which is not a selected single-select item
        if (!(menuItemRef.disabled || menuItemRef.selected && !menuItemRef.checkable)) {
          setTimeout(function () {
            menuItemRef.focusVisible();
          }, 20);
          return 1; // break
        }
      };
      for (var i = 0; i < menuItemsRef.current.length; i++) {
        if (_loop()) break;
      }
    }
  };
  var _children = useMemo(function () {
    menuItemsCountRef.current = 0;
    menuItemsRef.current = [];
    return React.Children.toArray(children).map(function (child) {
      if ( /*#__PURE__*/React.isValidElement(child) && child.type === EmbersMenuItem) {
        var menuItemsRefIndex = menuItemsCountRef.current++;
        return /*#__PURE__*/React.cloneElement(child, _objectSpread(_objectSpread({}, child.props), {}, {
          // we need to add an action to the appropriate menuItem,  so that we can automatically focus on it
          action: function action(ref) {
            if (ref) {
              menuItemsRef.current[menuItemsRefIndex] = _objectSpread(_objectSpread({}, ref), {}, {
                disabled: child.props.disabled,
                selected: child.props.selected,
                checkable: child.props.checkable
              });
              if (menuItemsCountRef.current === menuItemsRef.current.length) {
                focusOnMenuItem();
              }
            }
          }
        }));
      }
      return child;
    }).filter(React.isValidElement);
  }, [children]);
  if (open && anchorEl) {
    var listProps = _objectSpread(_objectSpread({
      role: role || 'listbox'
    }, ariaProps), otherProps);
    return /*#__PURE__*/React.createElement(EmbersPopper, {
      anchorEl: anchorEl,
      onEscape: handleClose,
      fullWidth: fullWidth,
      open: open,
      className: className,
      placement: placement,
      verticalOffset: verticalOffset,
      disableCloseOnWindowResize: disableCloseOnWindowResize,
      modifiers: modifiers,
      shouldPreventClose: shouldPreventClose
    }, title ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledMenuTitle, null, title), /*#__PURE__*/React.createElement(StyledTitledMenuList, listProps, _children)) : /*#__PURE__*/React.createElement(EmbersMenuList, listProps, _children));
  }
  return null;
};